import React from 'react';
import { Box, Typography, CircularProgress, Fade, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ErrorStatusTypes } from '../errorsDeclarations';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EditIcon from '@mui/icons-material/Edit';

interface AutoSaveIndicatorProps {
    isDirty: boolean;
    status: ErrorStatusTypes;
    customMessages?: {
        pending?: string;
        success?: string;
        error?: string;
        dirty?: string;
    };
    variant?: 'text' | 'chip';
    size?: 'small' | 'medium';
    showIcon?: boolean;
}

const AutoSaveIndicator: React.FC<AutoSaveIndicatorProps> = ({
                                                                 isDirty,
                                                                 status,
                                                                 customMessages = {},
                                                                 variant = 'text',
                                                                 size = 'small',
                                                                 showIcon = true,
                                                             }) => {
    const theme = useTheme();

    const defaultMessages = {
        pending: 'Salvataggio in corso...',
        success: 'Salvato',
        error: 'Errore nel salvataggio',
        dirty: 'Modifiche non salvate',
    };

    const messages = { ...defaultMessages, ...customMessages };

    const getStateConfig = () => {
        if (status === ErrorStatusTypes.PENDING) {
            return {
                color: theme.palette.info.main,
                message: messages.pending,
                icon: <CircularProgress size={12} sx={{ mr: 0.5 }} />,
            };
        }

        if (isDirty) {
            return {
                color: theme.palette.warning.main,
                message: messages.dirty,
                icon: showIcon ? <EditIcon sx={{ fontSize: 14, mr: 0.5 }} /> : null,
            };
        }

        if (status === ErrorStatusTypes.SUCCESS) {
            return {
                color: theme.palette.success.main,
                message: messages.success,
                icon: showIcon ? <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5 }} /> : null,
            };
        }

        if (status === ErrorStatusTypes.ERROR) {
            return {
                color: theme.palette.error.main,
                message: messages.error,
                icon: showIcon ? <ErrorIcon sx={{ fontSize: 14, mr: 0.5 }} /> : null,
            };
        }

        return null;
    };

    const stateConfig = getStateConfig();

    if (!stateConfig) return null;

    if (variant === 'chip') {
        return (
            <Fade in timeout={200}>
                <Chip
                    size={size}
                    label={stateConfig.message+' -- ' +status}
                    {...(stateConfig.icon && { icon: stateConfig.icon })}
                    sx={{
                        color: stateConfig.color,
                        borderColor: stateConfig.color,
                        backgroundColor: `${stateConfig.color}08`,
                        '& .MuiChip-icon': {
                            color: stateConfig.color,
                        },
                    }}
                    variant="outlined"
                />
            </Fade>
        );
    }

    return (
        <Fade in timeout={200}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: stateConfig.color,
                        display: 'flex',
                        alignItems: 'center',
                        fontWeight: 500,
                    }}
                >
                    {stateConfig.icon}
                    {stateConfig.message}
                </Typography>
            </Box>
        </Fade>
    );
};

export default AutoSaveIndicator;